const isEqual = (a: any, b: any): boolean => {
  // If one is null/undefined and the other isn't, they're not equal
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  
  if ((a === null || a === undefined) || (b === null || b === undefined)) {
    return false;
  }

  // Different types means not equal (except for number/string conversion for numeric values)
  if (typeof a !== typeof b) {
    // Special case for number/string comparison if both are numeric
    if ((typeof a === 'number' && typeof b === 'string') || 
        (typeof a === 'string' && typeof b === 'number')) {
      return String(a) === String(b);
    }
    return false;
  }

  if (typeof a === "object") {
    // Compare arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((v, i) => isEqual(v, b[i]));
    }

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    // Compare objects
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => isEqual(a[key], b[key]));
  }

  // Simple value comparison
  return a === b;
};

const getChangedValues = (initial: any, current: any): Record<string, any> => {
  const changes: Record<string, any> = {};

  if (!initial || !current) {
    return changes;
  }

  // Handle case where current or initial is completely different type or structure
  if (typeof initial !== typeof current) {
    return current;
  }

  // Get all keys from both objects to ensure we catch added properties too
  const allKeys = new Set([...Object.keys(initial), ...Object.keys(current)]);

  allKeys.forEach((key) => {
    // Check if the key exists in current but not in initial (new property)
    if (!(key in initial) && (key in current) && current[key] !== undefined) {
      changes[key] = current[key];
      return;
    }

    // Skip if key doesn't exist in current
    if (!(key in current)) {
      return;
    }

    // Handle nested objects recursively
    if (
      typeof initial[key] === "object" && 
      initial[key] !== null &&
      typeof current[key] === "object" && 
      current[key] !== null &&
      !Array.isArray(initial[key]) && 
      !Array.isArray(current[key]) &&
      !(initial[key] instanceof Date) && 
      !(current[key] instanceof Date)
    ) {
      const nestedChanges = getChangedValues(initial[key], current[key]);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = nestedChanges;
      }
    } 
    // Handle all other cases (primitives, arrays, dates, etc.)
    else if (!isEqual(initial[key], current[key])) {
      changes[key] = current[key];
    }
  });

  return changes;
};

export default getChangedValues;
